import { useMemo, useState } from 'react';

import BookCover from '@/components/v2/primitives/BookCover';
import Reveal from '@/components/v2/primitives/Reveal';
import { HUE_MARCA, hueForLivro } from '@/design/hues';

import { nomeDoMes } from './useHomeData';

const TODOS = 'todos';

const anoDe = (data) => {
  const d = new Date(data);
  return Number.isNaN(d.getTime()) ? null : d.getUTCFullYear();
};

/**
 * Acervo do clube.
 *
 * Substitui a estante horizontal com uma obra em destaque. O motivo é
 * de escala: o acervo cresce um livro por mês, e uma tira que rola de
 * lado funciona com seis capas, não com trinta. Aqui vira grade com
 * filtro por ano, que continua legível conforme o acervo engorda.
 */
export const LivrosQueJaLemos = ({ livros = [], carregando }) => {
  const [ano, setAno] = useState(TODOS);

  const anos = useMemo(() => {
    const encontrados = [
      ...new Set(livros.map((l) => anoDe(l.month)).filter(Boolean)),
    ];
    return [TODOS, ...encontrados.sort((a, b) => b - a)];
  }, [livros]);

  const visiveis = useMemo(
    () =>
      ano === TODOS ? livros : livros.filter((l) => anoDe(l.month) === ano),
    [livros, ano],
  );

  if (carregando) {
    return (
      <section
        className="mx-auto w-full max-w-[1100px] px-5 py-10 md:px-6"
        aria-busy="true"
      >
        <div className="h-72 animate-pulse bg-surface-container-low" />
      </section>
    );
  }

  if (!livros.length) return null;

  return (
    <section data-hue={HUE_MARCA} className="banda-creme">
      <div className="mx-auto w-full max-w-[1100px] px-5 py-14 md:px-6 md:py-20">
      <Reveal>
        <h2 className="type-club mb-3 text-[clamp(34px,6.4vw,76px)] text-[var(--banda-titulo)]">
          Livros Lidos
        </h2>
        <p className="mb-9 max-w-[52ch] text-[clamp(15.5px,1.8vw,18px)] opacity-85">
          Explore as leituras anteriores do Lily Book Club e reviva os
          debates.
        </p>

        {anos.length > 2 && (
          <div
            className="mb-7 flex flex-wrap gap-2"
            role="group"
            aria-label="Filtrar por ano"
          >
            {anos.map((a) => {
              const ativo = a === ano;
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAno(a)}
                  aria-pressed={ativo}
                  className={`rounded-full border px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-all duration-300 ${
                    ativo
                      ? 'border-[var(--banda-botao)] bg-[var(--banda-botao)] text-[var(--banda-botao-tinta)]'
                      : 'border-[var(--banda-fio)] opacity-75 hover:opacity-100'
                  }`}
                >
                  {a === TODOS ? 'Todos' : a}
                </button>
              );
            })}
          </div>
        )}

        <ul className="grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5">
          {visiveis.map((livro) => {
            const mes = nomeDoMes(livro.month);
            const anoLivro = anoDe(livro.month);

            return (
              <li key={livro.id} data-hue={hueForLivro(livro)}>
                <div className="block">
                  <div className="relative">
                    <BookCover
                      titulo={livro.title}
                      autor={livro.author}
                      etiqueta={anoLivro ? `${mes} ${anoLivro}` : mes}
                      capaBase64={livro.coverBase64}
                    />
                  </div>

                  <p className="mt-3 text-[13.5px] font-bold leading-tight">
                    {livro.title}
                  </p>
                  <p className="type-literary m-0 text-[12.5px] text-secondary">
                    {livro.author}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>

        {!visiveis.length && (
          <p className="py-8 italic text-secondary">
            Nenhuma leitura registrada em {ano} ainda.
          </p>
        )}
      </Reveal>
      </div>
    </section>
  );
};

export default LivrosQueJaLemos;
